import { IScope } from 'angular'
import {
  InitialQuickProduct,
  ProductType,
  VariantAttribute,
  VariantAttributeCombinationDetail,
} from '@vend/business-components'
import { ReactModalController } from '../../react/ReactModalController'
import { SaleManager } from '../../../managers/sale-manager'
import { SyncManager } from '../../../managers/sync-manager'
import { ProductsStore } from '../../../factories/database/products-store'
import {
  wrToastNegative,
  wrToastSuccess,
} from '../../react/helios/ToastNotifications/WrToastNotifications'

interface AddProductFastModalOptions {
  product: InitialQuickProduct
  productTypes: ProductType[]
  onClose: (event?: React.MouseEvent | KeyboardEvent) => void
  existingVariantAttributes?: VariantAttribute[]
  existingVariantCombinationDetails?: VariantAttributeCombinationDetail[]
  onBack?: () => void
}

interface Options {
  product: InitialQuickProduct
  productTypes: ProductType[]
  existingVariantAttributes?: VariantAttribute[]
  existingVariantCombinationDetails?: VariantAttributeCombinationDetail[]
  onBack?: () => void
}
/**
 * Controller class for AddProductFast (QUACK) modal
 *
 * @class AddProductFastModalController
 */
export class AddProductFastModalController extends ReactModalController {
  public options: AddProductFastModalOptions

  constructor(
    $scope: IScope,
    close: (val: Promise<any>) => void,
    vdEscapeStack: any,
    options: Options,
    private saleManager: SaleManager,
    private syncManager: SyncManager,
    private productsStore: ProductsStore
  ) {
    'ngInject'
    super($scope, close, vdEscapeStack)

    this.dismiss = () => this.close(Promise.resolve())

    this.options = {
      onClose: this.onClose.bind(this),
      product: options.product,
      productTypes: options.productTypes,
      existingVariantAttributes: options.existingVariantAttributes,
      existingVariantCombinationDetails:
        options.existingVariantCombinationDetails,
      onBack: options.onBack
        ? () => {
            this.dismiss()
            options.onBack!()
          }
        : undefined,
    }
  }

  private async getProductsFromDB(productIds: string[]) {
    const getProductFromDB = async (id: string) => {
      const product = await this.productsStore.get(id)
      if (product) {
        return product
      }
      return Promise.reject(new Error('Cannot get product from indexedDB'))
    }

    const products = await Promise.all(productIds.map(getProductFromDB))
    return products
  }

  private toastSuccess(productName: string) {
    if (this.options.existingVariantAttributes) {
      wrToastSuccess('variants.add.success', {
        productName,
      })
    } else {
      wrToastSuccess('product.add.success', { productName })
    }
  }

  private toastFailed(productName: string | undefined) {
    if (productName) {
      wrToastNegative('product.add.fail.name', { productName })
    }
    wrToastNegative('product.add.fail')
  }

  public async onClose(
    productId: string | undefined,
    variantIds: string[] | undefined
  ) {
    let productName: string | undefined
    let hasError = false
    if (productId) {
      try {
        await this.syncManager.sync()
        const products = await this.getProductsFromDB(
          variantIds ? [productId, ...variantIds] : [productId]
        )
        // productName might be undefined if previous steps fail.
        productName = products[0].name
        await Promise.all(
          products.map(prod => this.saleManager.addProductToSale(prod))
        )
      } catch {
        hasError = true
      }

      this.dismiss()

      if (hasError) {
        this.toastFailed(productName)
      } else {
        this.toastSuccess(productName ?? '')

        // Trigger a digest cycle to ensure that the UI reflects the latest data changes.
        this.$scope.$digest()
      }
    } else {
      this.dismiss()
    }
  }
}
